"use client";

import React, { useId } from "react";
import { cn } from "utils/utils";

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode; // For backwards compatibility, but doesn't render in native select
  iconClass?: string; // CSS class for icon fonts (e.g., "ms ms-riftbound") - works in native select
  // Note: React components in 'icon' prop won't render in native select elements
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

export type SelectItem = SelectOption | SelectOptionGroup;

function isOptionGroup(item: SelectItem): item is SelectOptionGroup {
  return "options" in item && Array.isArray(item.options);
}

export interface SelectProps {
  options: SelectItem[];
  name?: string;
  value: string | number | null;
  onChange: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  // dropdownClassName removed - not applicable to native select
  isReversed?: boolean;
  // selectedColor removed - use error/success states instead
  // maxHeight removed - native select handles scrolling
  // getOptionClassName removed - not supported by native select
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  isLoading?: boolean;
  // New DaisyUI-specific props
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "ghost";
  color?:
    | "primary"
    | "secondary"
    | "accent"
    | "info"
    | "success"
    | "warning"
    | "error";
  // Layout options
  layout?: "vertical" | "horizontal";
  labelWidth?: string; // Custom width for horizontal layout (e.g., "w-32", "w-1/4")
  // Width behavior
  autoWidth?: boolean; // Auto-size to fit longest option (default: true)
  icon?: React.ReactNode;
}

const DEFAULT_FOCUS =
  "focus:outline-base-content/50 focus-within:outline-base-content/50 focus:border-base-content/50 focus-within:border-base-content/50";

const COLOR_STYLE_MAP: Record<
  NonNullable<SelectProps["color"]>,
  { text: string; focus: string }
> = {
  primary: {
    text: "text-primary",
    focus:
      "focus:outline-primary/50 focus-within:outline-primary/50 focus:border-primary/50 focus-within:border-primary/50",
  },
  secondary: {
    text: "text-secondary",
    focus:
      "focus:outline-secondary/50 focus-within:outline-secondary/50 focus:border-secondary/50 focus-within:border-secondary/50",
  },
  accent: {
    text: "text-accent",
    focus:
      "focus:outline-accent/50 focus-within:outline-accent/50 focus:border-accent/50 focus-within:border-accent/50",
  },
  info: {
    text: "text-info",
    focus:
      "focus:outline-info/50 focus-within:outline-info/50 focus:border-info/50 focus-within:border-info/50",
  },
  success: {
    text: "text-success",
    focus:
      "focus:outline-success/50 focus-within:outline-success/50 focus:border-success/50 focus-within:border-success/50",
  },
  warning: {
    text: "text-warning",
    focus:
      "focus:outline-warning/50 focus-within:outline-warning/50 focus:border-warning/50 focus-within:border-warning/50",
  },
  error: {
    text: "text-error",
    focus:
      "focus:outline-error/50 focus-within:outline-error/50 focus:border-error/50 focus-within:border-error/50",
  },
};

export function Select({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  isReversed = false,
  label,
  description,
  error,
  required = false,
  isLoading = false,
  size = "md",
  variant,
  color,
  layout = "horizontal",
  labelWidth = "w-fit",
  autoWidth = true,
  icon,
  name = "",
  ...rest
}: SelectProps) {
  // IDs for ARIA relationships
  const selectId = useId();
  const labelId = useId();
  const descriptionId = useId();
  const errorId = useId();

  const sortedOptions = isReversed ? [...options].reverse() : options;

  // Flatten options for value lookup (handles both flat options and groups)
  const flattenedOptions = sortedOptions.flatMap((item) =>
    isOptionGroup(item) ? item.options : [item]
  );

  // Handle change from native select
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    // Convert to number if the original value was a number
    const option = flattenedOptions.find(
      (opt) => String(opt.value) === newValue
    );
    if (option) {
      onChange(option.value);
    }
  };

  // Build ARIA describedby
  const ariaDescribedBy =
    [description ? descriptionId : null, error ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const colorStyles = color ? COLOR_STYLE_MAP[color] : undefined;
  const colorTextClass = colorStyles?.text;


  // Build select classes
  const selectClasses = cn(
    "select",
    // Width behavior - auto-size to fit content using Tailwind utilities
    autoWidth ? "w-auto min-w-max" : "",
    // Variant (in v5, selects have border by default, use ghost to remove)
    variant === "ghost" && "select-ghost",
    // Size
    size === "xs" && "select-xs",
    size === "sm" && "select-sm",
    size === "lg" && "select-lg",
    // Color
    color === "primary" && "select-primary",
    color === "secondary" && "select-secondary",
    color === "accent" && "select-accent",
    color === "info" && "select-info",
    color === "success" && "select-success",
    color === "warning" && "select-warning",
    color === "error" && "select-error",
    // Error state
    error && "select-error",
    // Loading/disabled state
    isLoading && "select-disabled",
    // Text color for selected value
    colorTextClass,
    // Focus state
    colorStyles?.focus ?? DEFAULT_FOCUS,
    // Custom classes
    className
  );

  // Conditional rendering based on layout
  if (layout === "horizontal") {
    return (
      <div className={cn(className)}>
        {/* Description */}
        {description && (
          <div id={descriptionId} className="mb-1">
            <span className="text-base-content/70 text-sm">{description}</span>
          </div>
        )}

        {/* DaisyUI v5 wrapper pattern — explicit htmlFor ensures a11y tools
            recognise the label/control association even with DaisyUI wrapper classes */}
        <label className={selectClasses} htmlFor={selectId}>
          {/* sr-only only needed when there is no visible label — prevents empty-label
              a11y error without double-announcing when a visible label is present */}
          {!label && (
            <span className="sr-only">{placeholder}</span>
          )}
          {/* Left-side Label */}
          {label && (
            <span
              className={cn(
                "label",
                labelWidth,
                colorTextClass
              )}
              id={labelId}
            >
              {label}
              {required && <span className="text-error ml-1">*</span>}
            </span>
          )}

          <select
            id={selectId}
            value={value ?? ""}
            onChange={handleChange}
            disabled={disabled || isLoading}
            required={required}
            aria-labelledby={label ? labelId : undefined}
            aria-describedby={ariaDescribedBy}
            aria-invalid={error ? "true" : undefined}
            {...rest}
          >
            {/* Placeholder option */}
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}

            {/* Options (supports both flat options and option groups) */}
            {sortedOptions.map((item, index) =>
              isOptionGroup(item) ? (
                <optgroup key={`group-${index}`} label={item.label}>
                  {item.options.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      className={option.iconClass}
                    >
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ) : (
                <option
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                  className={item.iconClass}
                >
                  {item.label}
                </option>
              )
            )}
          </select>

          {/* Loading indicator */}
          {isLoading && (
            <span className="loading loading-spinner loading-xs opacity-60"></span>
          )}
        </label>

        {/* Error Message */}
        {error && (
          <div id={errorId} className="mt-1">
            <span className="text-error text-sm">{error}</span>
          </div>
        )}
      </div>
    );
  }

  // Default vertical layout (fieldset pattern)
  return (
    <fieldset className={cn("fieldset", className)}>
      {/* Legend (label) */}
      {label && (
        <legend className={cn("fieldset-legend", colorTextClass)}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </legend>
      )}

      <select
        id={selectId}
        value={value ?? ""}
        onChange={handleChange}
        disabled={disabled || isLoading}
        required={required}
        className={selectClasses}
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error ? "true" : undefined}
        {...rest}
      >
        {/* Placeholder option */}
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}

        {/* Options (supports both flat options and option groups) */}
        {sortedOptions.map((item, index) =>
          isOptionGroup(item) ? (
            <optgroup key={`group-${index}`} label={item.label}>
              {item.options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={option.iconClass}
                >
                  {option.label}
                </option>
              ))}
            </optgroup>
          ) : (
            <option
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              className={item.iconClass}
            >
              {item.label}
            </option>
          )
        )}
      </select>

      {/* Description as label (DaisyUI fieldset pattern) */}
      {description && <span className="label">{description}</span>}

      {/* Error Message */}
      {error && (
        <div id={errorId} className="mt-1">
          <span className="text-error text-sm">{error}</span>
        </div>
      )}
    </fieldset>
  );
}
